import { useEffect } from "react";

import appConfig from "config/appConfig";
import type { FallbackProps } from "react-error-boundary";
import { useNavigate } from "react-router-dom";

import useLocalStorage from "shared/services/localStorage/localStorage";

const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
	const { setWithExpiry, getWithExpiry } = useLocalStorage();
	const navigate = useNavigate();

	const errorInfo = error as {
		message?: string;
	};

	// Handles failed lazy loading of a JS/CSS chunk.
	useEffect(() => {
		const chunkFailedMessage = /Loading chunk [\d]+ failed/;
		const importModuleFailedMessage = /Importing a module script failed/;
		const invalidMimeTypeFailedMessage = /is not a valid JavaScript MIME type/;
		const dynamicImportMessage = /to fetch dynamically imported module/;

		if (
			errorInfo?.message &&
			(chunkFailedMessage.test(errorInfo.message) ||
				importModuleFailedMessage.test(errorInfo.message) ||
				invalidMimeTypeFailedMessage.test(errorInfo.message) ||
				dynamicImportMessage.test(errorInfo.message))
		) {
			if (!getWithExpiry("chunk_failed")) {
				setWithExpiry("chunk_failed", "true", 10000);
				window.location.reload();
			}
		}
	}, [errorInfo, getWithExpiry, setWithExpiry]);

	const handleGoHome = () => {
		resetErrorBoundary();
		navigate(appConfig.CORE_URLS.LAUNCH);
	};

	return (
		<div className="flex flex-col items-center justify-center min-h-screen p-8 text-center">
			<h1 className="text-2xl font-semibold text-gray-900 mb-4">Something went wrong</h1>
			<p className="text-gray-600 mb-6 max-w-md">
				We encountered an unexpected error. Please try again or return to the home page.
			</p>
			{import.meta.env.DEV && errorInfo?.message && (
				<pre className="bg-gray-100 p-4 rounded-lg text-left text-sm text-red-600 mb-6 max-w-2xl overflow-auto">
					{errorInfo.message}
				</pre>
			)}
			<div className="flex gap-4">
				<button
					onClick={resetErrorBoundary}
					className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
				>
					Try Again
				</button>
				<button
					onClick={handleGoHome}
					className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
				>
					Go to Home
				</button>
			</div>
		</div>
	);
};

export default ErrorFallback;
